import { useEffect, useState } from 'react'
import { OctagonX, Workflow, X } from 'lucide-react'
import type { BrokerSnapshot, PlaceOrderRequest } from '@shared/broker'
import type { BrokerName } from '@shared/brokers'
import { brokerApi, workersApi } from '../../lib/api'
import { useBrokerStream } from '../../hooks/useBrokerStream'
import { useWorkers } from '../../hooks/useWorker'
import { useStore } from '../../store'
import { dirClass, fmtMoney, fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'

const ACTIVE = new Set(['ApiPending', 'PendingSubmit', 'PreSubmitted', 'Submitted', 'PendingCancel'])
const CANCELLABLE = new Set(['ApiPending', 'PendingSubmit', 'PreSubmitted', 'Submitted'])

const field =
  'min-w-0 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[12.5px] text-fg placeholder:text-fg-faint focus:outline-none'

function Tile({ label, value, tone }: { label: string; value: string; tone?: number | null }) {
  return (
    <div className="min-w-0 rounded-lg border border-[var(--line-soft)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={cn('num mt-0.5 truncate text-[15px] font-semibold', tone == null ? 'text-fg' : dirClass(tone))}>{value}</div>
    </div>
  )
}

const signed = (n: number | null) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${fmtMoney(n)}`)

function OrderTicket({ snap }: { snap: BrokerSnapshot }) {
  const [symbol, setSymbol] = useState('')
  const [action, setAction] = useState<'BUY' | 'SELL'>('BUY')
  const [qty, setQty] = useState('')
  const [type, setType] = useState<PlaceOrderRequest['orderType']>('LMT')
  const [limit, setLimit] = useState('')
  const [stop, setStop] = useState('')
  const [tif, setTif] = useState<'DAY' | 'GTC'>('DAY')
  const [outsideRth, setOutsideRth] = useState(false)
  const [review, setReview] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  const needsLimit = type === 'LMT' || type === 'STP LMT'
  const needsStop = type === 'STP' || type === 'STP LMT'
  const req: PlaceOrderRequest = {
    symbol: symbol.trim().toUpperCase(),
    action,
    quantity: Math.floor(Number(qty)),
    orderType: type,
    limitPrice: needsLimit ? Number(limit) : undefined,
    stopPrice: needsStop ? Number(stop) : undefined,
    tif,
    outsideRth,
  }
  const valid =
    /^[A-Z][A-Z0-9.\-]{0,11}$/.test(req.symbol) &&
    req.quantity > 0 &&
    (!needsLimit || (req.limitPrice ?? 0) > 0) &&
    (!needsStop || (req.stopPrice ?? 0) > 0) &&
    !(outsideRth && (snap.status.broker === 'robinhood' ? type !== 'LMT' : type === 'MKT'))
  const enabled = snap.status.tradingEnabled
  const paper = snap.status.isPaper
  const summary = `${action} ${req.quantity || '—'} ${req.symbol || '—'} · ${type}${needsStop ? ` stop ${fmtPrice(req.stopPrice ?? null)}` : ''}${needsLimit ? ` limit ${fmtPrice(req.limitPrice ?? null)}` : ''} · ${tif}${outsideRth ? ' · outside RTH' : ''}`

  const submit = async () => {
    setBusy(true)
    setMsg(null)
    try {
      const r = await brokerApi.placeOrder(req, snap.status.broker)
      setMsg({ tone: 'ok', text: `Order #${r.orderId} sent to ${snap.status.label} ${snap.status.accounts[0] ?? ''}.` })
      setReview(false)
      setQty('')
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Order rejected' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card p-3.5">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-fg">{paper ? 'Paper order' : `${snap.status.label} order`}</span>
        <span className={cn('rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10px] font-semibold uppercase', enabled && !paper ? 'text-down' : 'text-fg-muted')}>
          {!enabled ? 'disabled' : paper ? 'paper' : 'real money'}
        </span>
      </div>
      {!enabled && <div className="mt-1.5 text-[11.5px] text-fg-subtle">{snap.status.tradingBlockedReason}</div>}
      <div className="mt-2.5 grid grid-cols-2 gap-1.5">
        <input value={symbol} onChange={(e) => { setSymbol(e.target.value); setReview(false) }} placeholder="Symbol (AAPL)" className={cn(field, 'col-span-2 uppercase')} />
        <div className="col-span-2 inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
          {(['BUY', 'SELL'] as const).map((a) => (
            <button
              key={a}
              onClick={() => { setAction(a); setReview(false) }}
              className={cn('flex-1 rounded-md py-1 text-[12px] font-semibold transition-colors', action === a ? (a === 'BUY' ? 'bg-[var(--color-surface-3)] text-up' : 'bg-[var(--color-surface-3)] text-down') : 'text-fg-subtle')}
            >
              {a}
            </button>
          ))}
        </div>
        <input value={qty} onChange={(e) => { setQty(e.target.value); setReview(false) }} placeholder="Quantity" inputMode="numeric" className={cn(field, 'num')} />
        <select value={type} onChange={(e) => { setType(e.target.value as PlaceOrderRequest['orderType']); setReview(false) }} className={field}>
          <option value="LMT">Limit</option>
          <option value="MKT">Market</option>
          <option value="STP">Stop</option>
          <option value="STP LMT">Stop limit</option>
        </select>
        {needsStop && <input value={stop} onChange={(e) => { setStop(e.target.value); setReview(false) }} placeholder="Stop price" inputMode="decimal" className={cn(field, 'num')} />}
        {needsLimit && <input value={limit} onChange={(e) => { setLimit(e.target.value); setReview(false) }} placeholder="Limit price" inputMode="decimal" className={cn(field, 'num')} />}
        <select value={tif} onChange={(e) => { setTif(e.target.value as 'DAY' | 'GTC'); setReview(false) }} className={field}>
          <option value="DAY">Day</option>
          <option value="GTC">GTC</option>
        </select>
        <label className="flex items-center gap-1.5 px-1 text-[11.5px] text-fg-muted">
          <input type="checkbox" checked={outsideRth} onChange={(e) => { setOutsideRth(e.target.checked); setReview(false) }} className="accent-[var(--color-accent)]" />
          Outside RTH
        </label>
      </div>

      {!review ? (
        <button
          onClick={() => setReview(true)}
          disabled={!enabled || !valid}
          className="mt-2.5 w-full rounded-lg border border-[var(--line-soft)] py-1.5 text-[12.5px] font-medium text-fg-muted transition-colors hover:text-fg disabled:opacity-40"
        >
          Review order
        </button>
      ) : (
        <div className="mt-2.5 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] p-2.5">
          <div className="num text-[12px] text-fg">{summary}</div>
          <div className="mt-0.5 text-[11px] text-fg-subtle">
            {snap.status.label} account {snap.status.accounts.join(', ')} ({paper ? 'paper' : 'real money'})
          </div>
          <div className="mt-2 flex gap-1.5">
            <button onClick={() => void submit()} disabled={busy} className="flex-1 rounded-lg bg-[var(--color-accent)] py-1.5 text-[12.5px] font-semibold text-[var(--color-base)] disabled:opacity-40">
              {busy ? 'Sending…' : 'Place order'}
            </button>
            <button onClick={() => setReview(false)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
              Edit
            </button>
          </div>
        </div>
      )}
      {msg && <div className={cn('mt-2 text-[11.5px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </div>
  )
}

/** Kill switch: stop every running worker (cancel its orders, close its shares at market). */
export function StopAllWorkers({ className }: { className?: string }) {
  const { workers } = useWorkers()
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), 6_000)
    return () => clearTimeout(t)
  }, [armed])
  const running = (workers ?? []).filter((w) => ['waiting', 'working', 'open', 'closing', 'error'].includes(w.status)).length
  if (!running && !msg) return null
  const go = async () => {
    setBusy(true)
    try {
      const r = await workersApi.killAll()
      setMsg(`Stopping ${r.stopped} worker${r.stopped === 1 ? '' : 's'}…`)
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Kill switch failed')
    } finally {
      setBusy(false)
      setArmed(false)
    }
  }
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {msg && <span className="text-[11px] text-fg-subtle">{msg}</span>}
      {running > 0 && (
        <button
          onClick={() => (armed ? void go() : setArmed(true))}
          disabled={busy}
          title="Cancel every worker order and close worker positions at market. Manual positions are untouched."
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-semibold transition-colors disabled:opacity-40',
            armed ? 'bg-down text-white' : 'border border-[var(--line-soft)] text-down hover:bg-[var(--color-surface-2)]',
          )}
        >
          <OctagonX size={13} />
          {armed ? `Confirm — stop ${running} worker${running === 1 ? '' : 's'} & close` : 'Stop all workers'}
        </button>
      )}
    </span>
  )
}

/** A connected broker's live account: values, positions (tagged with their workers), orders, fills, a ticket. */
export function BrokerPanel({ broker }: { broker: BrokerName }) {
  const { snapshot: snap, live } = useBrokerStream(true, broker)
  const focusSettings = useStore((s) => s.focusSettings)
  const openWorker = useStore((s) => s.openWorker)
  const [showExec, setShowExec] = useState(false)
  const [cancelErr, setCancelErr] = useState<string | null>(null)

  if (!snap) return null
  const st = snap.status
  const connected = st.connection === 'connected'
  // A broker that is not in use takes no room: IBKR with its gateway off, Robinhood without a link.
  // (A login in progress or a failed one stays visible — that is something to act on.)
  if (!connected) {
    if (!st.credentials.configured) return null
    if (broker === 'ibkr' && (st.gateway.phase === 'absent' || st.gateway.phase === 'stopped')) return null
  }

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line-soft)] px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        <span className={cn('h-1.5 w-1.5 rounded-full', connected && live ? 'bg-up' : st.gateway.phase === 'awaiting_2fa' ? 'animate-pulse bg-fg-muted' : 'bg-flat')} />
        <span className="text-[13px] font-semibold text-fg">{broker === 'ibkr' ? 'Interactive Brokers' : st.label}</span>
        {st.accounts.length > 0 && <span className="num text-[11.5px] text-fg-subtle">{st.accounts.join(', ')}</span>}
        {connected && (
          <span className={cn('rounded px-1 text-[9.5px] font-semibold uppercase', st.isPaper ? 'bg-[var(--color-inset)] text-fg-muted' : 'text-down')}>
            {st.isPaper ? 'paper' : broker === 'ibkr' ? 'not paper' : 'real money'}
          </span>
        )}
      </div>
      <span className="flex items-center gap-3">
        {connected && <StopAllWorkers />}
        <button onClick={() => focusSettings(broker === 'ibkr' ? 'broker' : 'robinhood')} className="text-[11.5px] text-fg-subtle hover:text-fg">
          Connection settings
        </button>
      </span>
    </div>
  )

  if (!connected) {
    const hint = broker === 'robinhood'
      ? (st.tradingBlockedReason ?? st.lastError ?? 'Connecting to Robinhood…')
      : !st.credentials.configured
      ? 'Connect your IBKR account to see live positions, P&L and orders here.'
      : st.gateway.phase === 'awaiting_2fa'
        ? 'Waiting for your approval — confirm the login in IBKR Mobile.'
        : st.gateway.phase === 'login_failed'
          ? 'IBKR rejected the login — re-enter your username/password in Settings.'
          : st.gateway.phase === 'absent' || st.gateway.phase === 'stopped'
            ? 'The gateway is not running — start it from Settings.'
            : 'Connecting to IBKR…'
    return (
      <div className="card mt-5 overflow-hidden">
        {header}
        <div className="px-4 py-6 text-center text-[12.5px] text-fg-subtle">{hint}</div>
      </div>
    )
  }

  const s = snap.summary
  const cancel = async (id: number) => {
    setCancelErr(null)
    try {
      await brokerApi.cancelOrder(id)
    } catch (e) {
      setCancelErr(e instanceof Error ? e.message : 'Cancel failed')
    }
  }
  const openOrders = snap.orders.filter((o) => ACTIVE.has(o.status))

  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_300px]">
      <div className="card min-w-0 overflow-hidden">
        {header}
        <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-5">
          <Tile label={broker === 'ibkr' ? 'Net liquidation' : 'Account value'} value={fmtMoney(s.netLiquidation)} />
          <Tile label="Cash" value={fmtMoney(s.totalCash)} />
          <Tile label="Buying power" value={fmtMoney(s.buyingPower)} />
          <Tile label="Day P&L" value={signed(snap.pnl.daily)} tone={snap.pnl.daily} />
          <Tile label="Unrealized" value={signed(snap.pnl.unrealized)} tone={snap.pnl.unrealized} />
        </div>

        <div className="overflow-x-auto border-t border-[var(--line-soft)]">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-fg-faint">
                <th className="px-3 py-2 text-left">Position</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Avg cost</th>
                <th className="px-3 py-2 text-right">Mark</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-right">Unrealized</th>
                <th className="px-3 py-2 text-left">Managed by</th>
              </tr>
            </thead>
            <tbody>
              {snap.positions.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-fg-subtle">No positions.</td></tr>
              ) : (
                snap.positions.map((p) => {
                  const ws = p.workers ?? []
                  const manual = Math.abs(p.quantity) - ws.reduce((a, w) => a + w.open, 0)
                  return (
                    <tr key={`${p.account}-${p.conId ?? p.symbol}`} className="border-t border-[var(--separator)]">
                      <td className="px-3 py-2 font-medium text-fg">{p.symbol}</td>
                      <td className="num px-3 py-2 text-right text-fg-muted">{p.quantity}</td>
                      <td className="num px-3 py-2 text-right text-fg-muted">{fmtPrice(p.avgCost)}</td>
                      <td className="num px-3 py-2 text-right text-fg">{fmtPrice(p.marketPrice)}</td>
                      <td className="num px-3 py-2 text-right text-fg">{fmtMoney(p.marketValue)}</td>
                      <td className={cn('num px-3 py-2 text-right', dirClass(p.unrealizedPnL))}>{signed(p.unrealizedPnL)}</td>
                      <td className="px-3 py-2 text-left">
                        <span className="flex flex-wrap items-center gap-1">
                          {ws.map((w) => (
                            <button
                              key={w.id}
                              onClick={() => openWorker(w.id)}
                              title={`Worker “${w.name}” holds ${w.open} of these`}
                              className="inline-flex max-w-[140px] items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-muted hover:text-fg"
                            >
                              <Workflow size={10} className="shrink-0" />
                              <span className="truncate">{w.name}</span>
                              <span className="num text-fg-faint">{w.open}</span>
                            </button>
                          ))}
                          {manual > 0 && (
                            <span className="text-[10.5px] text-fg-faint">{ws.length ? `+${manual} manual` : 'manual'}</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-[var(--line-soft)]">
          <div className="px-3.5 py-2 text-[11px] uppercase tracking-wide text-fg-faint">Open orders ({openOrders.length})</div>
          {openOrders.length === 0 ? (
            <div className="px-3.5 pb-3 text-[12px] text-fg-subtle">No working orders.</div>
          ) : (
            <div className="divide-y divide-[var(--separator)]">
              {openOrders.map((o) => (
                <div key={o.orderId} className="flex items-center justify-between gap-2 px-3.5 py-2 text-[12.5px]">
                  <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                    <span className={cn('font-semibold', o.action === 'BUY' ? 'text-up' : 'text-down')}>{o.action}</span>
                    <span className="num text-fg">{o.quantity}</span>
                    <span className="font-medium text-fg">{o.symbol}</span>
                    <span className="num text-fg-subtle">
                      {o.orderType}
                      {o.stopPrice != null && ` stp ${fmtPrice(o.stopPrice)}`}
                      {o.limitPrice != null && ` lmt ${fmtPrice(o.limitPrice)}`} · {o.tif}
                    </span>
                    <span className="text-[11px] text-fg-faint">{o.status}{o.filled > 0 ? ` · ${o.filled} filled` : ''}</span>
                    {o.workerId && (
                      <button
                        onClick={() => openWorker(o.workerId!)}
                        className="inline-flex items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-muted hover:text-fg"
                        title="Placed by a trade worker — manage it from the worker"
                      >
                        <Workflow size={10} /> worker
                      </button>
                    )}
                    {o.ticketId && (
                      <span className="inline-flex items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-muted" title="Placed from this app (an approved order card)">
                        app order
                      </span>
                    )}
                  </span>
                  {CANCELLABLE.has(o.status) && (
                    <button onClick={() => void cancel(o.orderId)} className="shrink-0 rounded p-1 text-fg-subtle hover:text-down" title="Cancel order">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {cancelErr && <div className="px-3.5 pb-2 text-[11.5px] text-down">{cancelErr}</div>}
        </div>

        {snap.executions.length > 0 && (
          <div className="border-t border-[var(--line-soft)] px-3.5 py-2">
            <button onClick={() => setShowExec((v) => !v)} className="text-[12px] font-medium text-fg-subtle hover:text-fg">
              {showExec ? '▾' : '▸'} Today’s fills ({snap.executions.length})
            </button>
            {showExec && (
              <div className="mt-1.5 divide-y divide-[var(--separator)]">
                {snap.executions.map((x) => (
                  <div key={x.execId} className="flex items-center justify-between py-1.5 text-[12px]">
                    <span className="flex items-center gap-2">
                      <span className={cn('font-semibold', x.side === 'BOT' ? 'text-up' : 'text-down')}>{x.side}</span>
                      <span className="num text-fg">{x.shares}</span>
                      <span className="text-fg-muted">{x.symbol}</span>
                      <span className="num text-fg-subtle">@ {fmtPrice(x.price)}</span>
                    </span>
                    <span className="num text-fg-faint">{x.time.split(' ').slice(-2).join(' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="min-w-0 self-start lg:sticky lg:top-2">
        <OrderTicket snap={snap} />
      </div>
    </div>
  )
}

/** The IBKR panel (kept for callers that still name it). */
export const IbkrPanel = () => <BrokerPanel broker="ibkr" />
